import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme as useMuiTheme,
} from "@mui/material";
import {
  Database,
  FolderOpen,
  LayoutDashboard,
  MessageSquare,
  ScrollText,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { Logo } from "../../../common/components/Logo";
import { useTheme } from "../../../common/contexts/ThemeContext";

const SIDEBAR_WIDTH = 275;

function Sidebar() {
  const { t } = useTranslation("admin_panel");
  const { colors } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 855) {
        setSidebarOpen(false);
        document.documentElement.style.setProperty("--sidebar-width", "0");
      } else {
        setSidebarOpen(true);
        document.documentElement.style.setProperty(
          "--sidebar-width",
          `${SIDEBAR_WIDTH}px`,
        );
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
    if (!sidebarOpen) {
      document.documentElement.style.setProperty(
        "--sidebar-width",
        `${SIDEBAR_WIDTH}px`,
      );
    } else {
      document.documentElement.style.setProperty("--sidebar-width", "0");
    }
  };

  const isActivePage = (path) => {
    return location.pathname === path;
  };

  const SidebarContent = () => (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <List sx={{ px: 2 }}>
          <Box sx={{ py: 2, textAlign: "center" }}>
            <Link
              to="/"
              style={{ textDecoration: "none", display: "inline-block" }}
            >
              <Logo alt="logo" style={{ height: 40, cursor: "pointer" }} />
            </Link>
          </Box>
          <ListItem disablePadding sx={{ mt: 3 }}>
            <ListItemButton
              component={Link}
              to="/admin/dashboard"
              selected={isActivePage("/admin/dashboard")}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                "&.Mui-selected": {
                  backgroundColor: "action.selected",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LayoutDashboard
                  size={20}
                  color={colors.text.primary}
                  strokeWidth={1.5}
                />
              </ListItemIcon>
              <ListItemText
                primary={t("sidebar.dashboard")}
                primaryTypographyProps={{ variant: "bodySm" }}
              />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/admin/users"
              selected={isActivePage("/admin/users")}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                "&.Mui-selected": {
                  backgroundColor: "action.selected",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Users
                  size={20}
                  color={colors.text.primary}
                  strokeWidth={1.5}
                />
              </ListItemIcon>
              <ListItemText
                primary={t("sidebar.users")}
                primaryTypographyProps={{ variant: "bodySm" }}
              />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/admin/projects"
              selected={isActivePage("/admin/projects")}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                "&.Mui-selected": {
                  backgroundColor: "action.selected",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <FolderOpen
                  size={20}
                  color={colors.text.primary}
                  strokeWidth={1.5}
                />
              </ListItemIcon>
              <ListItemText
                primary={t("sidebar.projects")}
                primaryTypographyProps={{ variant: "bodySm" }}
              />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/admin/sources"
              selected={isActivePage("/admin/sources")}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                "&.Mui-selected": {
                  backgroundColor: "action.selected",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Database
                  size={20}
                  color={colors.text.primary}
                  strokeWidth={1.5}
                />
              </ListItemIcon>
              <ListItemText
                primary={t("sidebar.sources")}
                primaryTypographyProps={{ variant: "bodySm" }}
              />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/admin/support"
              selected={isActivePage("/admin/support")}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                "&.Mui-selected": {
                  backgroundColor: "action.selected",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <MessageSquare
                  size={20}
                  color={colors.text.primary}
                  strokeWidth={1.5}
                />
              </ListItemIcon>
              <ListItemText
                primary={t("sidebar.support")}
                primaryTypographyProps={{
                  variant: "bodySm",
                }}
              />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/admin/audit-logs"
              selected={isActivePage("/admin/audit-logs")}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                "&.Mui-selected": {
                  backgroundColor: "action.selected",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <ScrollText
                  size={20}
                  color={colors.text.primary}
                  strokeWidth={1.5}
                />
              </ListItemIcon>
              <ListItemText
                primary={t("sidebar.auditLogs")}
                primaryTypographyProps={{ variant: "bodySm" }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={sidebarOpen}
        onClose={toggleSidebar}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: SIDEBAR_WIDTH,
            backgroundColor: colors.glass?.background || colors.pageSecondary,
            backdropFilter: colors.glass?.blur
              ? `blur(${colors.glass.blur}) saturate(180%)`
              : "none",
            borderRight: `1px solid ${colors.glass?.border || colors.border.primary}`,
            backgroundImage: `linear-gradient(180deg, ${muiTheme.palette.primary.main}14 0%, ${muiTheme.palette.primary.main}08 40%, ${muiTheme.palette.primary.main}05 100%)`,
          },
        }}
      >
        <SidebarContent />
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={sidebarOpen}
      sx={{
        width: sidebarOpen ? SIDEBAR_WIDTH : 0,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: SIDEBAR_WIDTH,
          minWidth: SIDEBAR_WIDTH,
          maxWidth: SIDEBAR_WIDTH,
          boxSizing: "border-box",
          overflow: "hidden",
          overflowY: "auto",
          backgroundColor: colors.glass?.background || colors.pageSecondary,
          backdropFilter: colors.glass?.blur
            ? `blur(${colors.glass.blur}) saturate(180%)`
            : "none",
          borderRight: `1px solid ${colors.glass?.border || colors.border.primary}`,
          backgroundImage: `linear-gradient(180deg, ${muiTheme.palette.primary.main}14 0%, ${muiTheme.palette.primary.main}08 40%, ${muiTheme.palette.primary.main}05 100%)`,
        },
      }}
    >
      <SidebarContent />
    </Drawer>
  );
}

export default Sidebar;
